/**
 * @file rich-text.component.tsx
 * @description Collapsed form-control компонент для работы с форматированным текстом в свернутом состоянии. Использует ReactQuill для редактирования rich-text контента с поддержкой различных форматов текста, списков, ссылок и заголовков. Имеет два режима работы: просмотр (показывает отформатированный текст) и редактирование (показывает полнофункциональный редактор). Обеспечивает экономию места в формах Task Manager за счет возможности сворачивания.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-01-15
 * @dependencies ['react', '@consta/uikit/Collapse', 'react-hook-form', '@components/ui/base/button', 'react-quill-new', 'react-quill-new/dist/quill.snow.css', '@components/ui/layout/typography-container', 'classnames']
 * @related-files ['@components/form-controls/rich-text/rich-text.component.tsx', '@components/form-controls/collapsed/attachments-rich-text/attachments-rich-text.component.tsx']
 */

import { useRef, useState } from 'react';
import { Collapse } from '@consta/uikit/Collapse';
import { Control, Controller } from 'react-hook-form';
import { Button } from '@components/ui/base/button';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { TypographyContainer } from '@components/ui/layout/typography-container';
import cn from 'classnames';

/**
 * Props для collapsed form-control компонента RichText с интеграцией react-hook-form
 */
interface IRichText {
    /** Текст заголовка для свернутой секции */
    label?: string;
    /** Имя поля в форме */
    name: string;
    /** Control объект из react-hook-form для управления формой */
    control: Control;
    /** Плейсхолдер для редактора */
    placeholder?: string;
}

export const RichText = ({ label, control, name, placeholder }: IRichText) => {
    const [isOpen, setOpen] = useState<boolean>(true);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const newValue = useRef<string | undefined>(undefined);

    const modules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
            ['link'],
            ['clean']
        ]
    };

    const formats = ['header', 'bold', 'italic', 'blockquote', 'list', 'bullet', 'indent', 'link'];

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <Collapse
                    label={label}
                    isOpen={isOpen}
                    onClick={(e: React.MouseEvent) => {
                        if ((e.target as HTMLDivElement).closest('.collapse-control')) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                        }

                        setOpen(!isOpen);
                    }}
                >
                    {isEdit ? (
                        <div className="block relative h-full w-full">
                            <ReactQuill
                                theme="snow"
                                value={newValue.current === undefined ? field.value : newValue.current}
                                onChange={(value: string) => (newValue.current = value)}
                                modules={modules}
                                formats={formats}
                            />

                            <div className="absolute flex gap-default right-[1.2698vmin] bottom-[1.2698vmin]">
                                <Button
                                    className="text-[1.482vmin]"
                                    label="Отменить"
                                    onClick={() => {
                                        newValue.current = field.value;
                                        setIsEdit(false);
                                    }}
                                    view="ghost"
                                />

                                <Button
                                    className="text-[1.482vmin]"
                                    label="ОК"
                                    onClick={() => {
                                        field.onChange(newValue.current);
                                        setIsEdit(false);
                                    }}
                                    view="primary"
                                />
                            </div>
                        </div>
                    ) : (
                        <TypographyContainer>
                            <span
                                onClick={() => setIsEdit(true)}
                                className={cn('cursor-pointer', {
                                    'text-[#3C3C4399]': !field.value,
                                    'block w-[62.49069vmin]': !field.value
                                })}
                                dangerouslySetInnerHTML={{ __html: field.value || placeholder || 'Нет данных' }}
                            />
                        </TypographyContainer>
                    )}
                </Collapse>
            )}
        />
    );
};
